import type { Doc } from "building-types/doc";
import type { Formula } from "building-types/options/formula";
import type { DocOptionLock } from "building-types/options/optionSet";
import type { DocOptionSetOverrides } from "building-types/options/optionTemplateTypes";
import type { RWTable, Table } from "common/data/table";
import objectIsEmpty from "common/utils/objectIsEmpty";
import type { Mutable } from "common/utils/types";
import uid from "common/utils/uid";
import { docGetActiveOptionTemplateOverrides } from "../docGetOptionTemplates";

const optionLocksByOptionSetIdCache = new WeakMap<Table<DocOptionLock>, Table<DocOptionLock>>();

/** Build optionSetId → lock. Prefer {@link getOptionLocksByOptionSetId} for repeated lookups. */
export function buildOptionLocksByOptionSetId(optionLocks: Doc["optionLocks"]): RWTable<DocOptionLock> {
  const byOptionSetId: RWTable<DocOptionLock> = {};
  for (const lockId in optionLocks) {
    const lock = optionLocks[lockId];
    byOptionSetId[lock.optionSetId] = lock;
  }
  return byOptionSetId;
}

/** Cached optionSetId → lock index. Invalidates when the locks table identity changes. */
export function getOptionLocksByOptionSetId(optionLocks: Doc["optionLocks"]): Table<DocOptionLock> {
  const cached = optionLocksByOptionSetIdCache.get(optionLocks);
  if (cached) {
    return cached;
  }
  const byOptionSetId = buildOptionLocksByOptionSetId(optionLocks);
  optionLocksByOptionSetIdCache.set(optionLocks, byOptionSetId);
  return byOptionSetId;
}

export function mergeDocOptionTemplatesIntoOptionLocks(
  doc: Pick<Doc, "optionTemplates" | "optionSetOverrides" | "optionLocks" | "optionSets">,
): Doc["optionLocks"] {
  // Create a copy of our original Option Locks
  const optionLocks: Mutable<Doc["optionLocks"]> = { ...doc.optionLocks };
  // Local index — do not use the WeakMap cache while mutating this working copy.
  const locksByOptionSetId = buildOptionLocksByOptionSetId(optionLocks);

  const optionSetOverrides = docGetActiveOptionTemplateOverrides(doc);
  for (const optionSetId in optionSetOverrides) {
    const optionSet = doc.optionSets[optionSetId];
    if (!optionSet) {
      // Skip if the option set doesn't exist on the doc
      continue;
    }
    const { disconnectCondition, disconnectConditionOptions } = optionSetOverrides[optionSetId];
    const optionLock = locksByOptionSetId[optionSetId];

    // Overwrite the existing conditions
    if (optionLock) {
      const updated: DocOptionLock = {
        ...optionLock,
        disconnectCondition: disconnectCondition ?? optionLock.disconnectCondition,
        disconnectConditionOptions: {
          ...optionLock.disconnectConditionOptions,
          ...disconnectConditionOptions,
        },
      };
      optionLocks[optionLock.id] = updated;
      locksByOptionSetId[optionSetId] = updated;
      // Or add a new one
    } else {
      const newOptionLock: DocOptionLock = {
        id: uid(),
        optionSetId,
        disconnectCondition,
        disconnectConditionOptions,
        entityType: "OPTION_LOCK",
        sortIndex: 0,
        name: optionSet.name,
      };
      optionLocks[newOptionLock.id] = newOptionLock;
      locksByOptionSetId[optionSetId] = newOptionLock;
    }
  }
  return optionLocks;
}

export function getOptionLockByOptionSetId(optionLocks: Doc["optionLocks"], optionSetId: string) {
  return getOptionLocksByOptionSetId(optionLocks)[optionSetId];
}

/** Merge lock + template-override disconnectConditionOptions for one option set. */
export function getDisconnectConditionOptions(
  optionLock: DocOptionLock | undefined,
  optionSetOverrides?: DocOptionSetOverrides,
): Table<Formula> {
  const originalConditions = optionLock?.disconnectConditionOptions ?? {};
  const overrideConditions = optionSetOverrides?.disconnectConditionOptions ?? {};
  return { ...originalConditions, ...overrideConditions };
}

/**
 * Build optionSetId → optionId → Formula for all disconnect-condition options.
 * Pass optionSetOverrides when template overrides should be merged (Studio path);
 * omit / pass {} to match pricing-path optionSetStateSetFromBuilding (locks only).
 */
export function buildDisconnectConditionOptionsTable(
  doc: Pick<Doc, "optionLocks">,
  optionSetOverrides: Table<DocOptionSetOverrides> = {},
): Table<Table<Formula>> {
  const result: RWTable<RWTable<Formula>> = {};

  for (const optionLock of Object.values(doc.optionLocks)) {
    const conditions = getDisconnectConditionOptions(optionLock, optionSetOverrides[optionLock.optionSetId]);
    if (!objectIsEmpty(conditions)) {
      result[optionLock.optionSetId] = conditions;
    }
  }

  for (const optionSetId in optionSetOverrides) {
    if (result[optionSetId]) {
      continue;
    }
    const conditions = getDisconnectConditionOptions(undefined, optionSetOverrides[optionSetId]);
    if (!objectIsEmpty(conditions)) {
      result[optionSetId] = conditions;
    }
  }

  return result;
}
